/**
 * web/src/shell/dest.ts — the shell's routing vocabulary, moved verbatim out
 * of App.tsx (card WUHnArc6lNID): the Tab union, the Dest deep-link type with
 * its hash codec, and the compose-menu route table. App.tsx re-exports the
 * public names, so imports of `Dest` / `ComposeIntent` from './App' (Thread,
 * FirstAct) keep working unchanged.
 */
import type { ComposeTile } from '../components/ComposeMenu';

export type Tab = 'thread' | 'us' | 'life' | 'notebook' | 'search';

/** Explicit destination (card Mn55eOvFgvC0, review H4): a deep link names its
 *  target section AND subsection (plus an optional entity) so it always lands
 *  where it promises — not on whichever subsection was visited last. Every
 *  card callback collapses to one of these, routed through navigateTo. */
export interface Dest {
  section: Tab;
  subsection?: string;
  entityId?: string;
}

/** Serialize a destination to its URL hash (#us-game, #life-plan, …) so the
 *  visible screen is modelled in the address and browser Back retraces it. */
export function destHash(dest: Dest): string {
  return `#${dest.section}${dest.subsection ? `-${dest.subsection}` : ''}`;
}

/** Parse a destination hash back into a Dest (null if it isn't one). The
 *  inverse of destHash — the hashchange listener uses it to re-apply the
 *  screen a Back/Forward navigation landed on. */
export function destFromHash(hash: string): Dest | null {
  const m = /^#(thread|us|life|notebook|search)(?:-([a-z]+))?$/.exec(hash);
  return m ? { section: m[1] as Tab, subsection: m[2] } : null;
}

/** A compose-menu pick carried to a destination panel so it can open its own
 *  composer / action rather than just switching tabs (design v1.1 §5.2: the
 *  + menu is feature discovery — one tap shows everything Pear can create,
 *  and tapping one should land on THAT action). Consumed on mount. */
export type ComposeIntent =
  | { kind: 'ask' }
  | { kind: 'question' }
  | { kind: 'note' }
  | { kind: 'idea' }
  | { kind: 'memory' }
  | { kind: 'date' }
  | { kind: 'link' }
  | { kind: 'kudos' }
  | null;

/** Where each compose tile lands (card RvESgw7lVASX). The + menu is feature
 *  discovery, so every tile picks a target tab AND carries the intent the
 *  destination panel opens its own composer from (design v1.1 §5.2) — a tap
 *  has to end on the actual action, not on a tab switch. A lookup table
 *  rather than the nested ternary it replaced: nine tiles do not read as a
 *  conditional chain. `message` is handled before the lookup — it stays in
 *  the Thread and clears the intent. */
interface ComposeRoute {
  intent: ComposeIntent;
  tab: Tab;
  /** Sub-section to select on arrival (card Mn55eOvFgvC0, H4) — a tile that
   *  promises a specific surface (Plan, Dates, …) must land on it, not on the
   *  tab's default. */
  subsection?: string;
}

export const COMPOSE_ROUTE: Record<Exclude<ComposeTile, 'message'>, ComposeRoute> = {
  ask: { intent: { kind: 'ask' }, tab: 'life' },
  question: { intent: { kind: 'question' }, tab: 'us' },
  kudos: { intent: { kind: 'kudos' }, tab: 'thread' },
  note: { intent: { kind: 'note' }, tab: 'notebook' },
  idea: { intent: { kind: 'idea' }, tab: 'notebook' },
  memory: { intent: { kind: 'memory' }, tab: 'notebook' },
  date: { intent: { kind: 'date' }, tab: 'notebook' },
  link: { intent: { kind: 'link' }, tab: 'notebook' },
};

/** Narrow ComposeIntent down to the tiles the Notebook panel acts on. */
export function notebookIntent(intent: ComposeIntent) {
  return intent &&
    (intent.kind === 'note' ||
      intent.kind === 'idea' ||
      intent.kind === 'memory' ||
      intent.kind === 'date' ||
      intent.kind === 'link')
    ? intent
    : null;
}
